import { ReactNode, useState, useEffect } from 'react';
import { LucideIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';

interface InfoSectionProps {
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  storageKey?: string; // Optional key for localStorage persistence
}

export function InfoSection({
  title,
  icon: Icon,
  children,
  collapsible = false,
  defaultExpanded = true,
  className = '',
  storageKey
}: InfoSectionProps) {
  // Load initial state from localStorage if storageKey is provided
  const [isExpanded, setIsExpanded] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved !== null) {
          return JSON.parse(saved);
        }
      } catch (error) {
        console.error('Failed to load section state from localStorage:', error);
      }
    }
    return defaultExpanded;
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(isExpanded));
      } catch (error) {
        console.error('Failed to save section state to localStorage:', error);
      }
    }
  }, [isExpanded, storageKey]);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            {Icon && <Icon className="h-5 w-5 text-gray-600" />}
            {title}
          </CardTitle>
          
          {collapsible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 w-8 p-0 hover:bg-gray-100 rounded"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      
      {(!collapsible || isExpanded) && (
        <CardContent className="space-y-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
}